
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import {addComma} from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import moment from 'moment';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import SalesOrderConversionRateBarChart from '@/components/reports-v3/components/codedWidgets/sales/SalesOrderConversionRateBarChart.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        TableFilterable,
        TableCollapseToggle,
        SalesOrderConversionRateBarChart,
    },
})
export default class SalesPendingSalesOrderTable extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales'];
    }

    public get currency() {
        return gdbx.currencySymbol;
    }

    public get dateFormatted() {
        return [this.selectedAsOfDate];
    }

    public get exportFileName() {
        const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');
        return 'Pending Sales Order' + '_' + 'As Of ' + formattedDate;
    }

    public get expensiveHook() {
        const {
            selectedAgents,
            selectedCustomers,
            selectedAreas,
        } = this;
        return JSON.stringify([
            selectedAgents,
            selectedCustomers,
            selectedAreas,
        ]);
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'areas'
    > = [];

    public validityDays: number = 30;

    public tableItems: Array<{
        doc_no: string;
        doc_date: number;
        customer: string;
        amount: number;
        os_amount: number;
        item: number;
        os_item: number;
        items: any;
    }> = [];
    public tableFields: any[] = [];
    public detailFields: any[] = [];
    public detailFields2: any[] = [];

    public amountSum: number = 0;
    public osAmountSum: number = 0;
    public itemSum: number = 0;
    public osItemSum: number = 0;

    public periodTotalData: any[] = [];
    public periodTotalFields: any[] = [
        {
            key: 'order_amt',
            label: 'Total Amount',
            textAlign: 'center',
        },
        {
            key: 'os_amt',
            label: 'Total O/S Amount',
            textAlign: 'center',
        },
        {
            key: 'No_of_os_order',
            label: 'Total Item',
            textAlign: 'center',
        },
        {
            key: 'No_of_os_customer',
            label: 'Total O/S Item',
            textAlign: 'center',
        },
    ];

    public infoModal = {
        id: 'info-modal2',
        index: 0,
        title: '',
        extras: '',
        amount: '',
        docDate: 0,
        os_amount: '',
        items: [] as any[],
    };
    public format(
        value: number,
        decimal: number = 2,
        forceDecimal: boolean = true,
    ) {
        return addComma(value, decimal, forceDecimal);
    }

    public info(item, extras, index, title, amount: any, osAmount, link) {
        this.infoModal.title = title + ' ' + '( ' + item.items[0].customer + ' )';
        this.infoModal.extras = extras;
        this.infoModal.index = index;
        this.infoModal.amount = this.format(Math.abs(amount));
        this.infoModal.os_amount = this.format(osAmount);
        this.infoModal.docDate = item.items[0].docDate;
        this.infoModal.items = item.items;
        this.$root.$emit('bv::show::modal', this.infoModal.id, link);
    }

    public resetInfoModal() {
        this.infoModal.title = '';
        this.infoModal.extras = '';
        this.infoModal.items = [];
    }

    /**
     * Load detail rows for a single docNo on demand (when dialog opens).
     */
    public async loadDetailForDoc(docNo: string): Promise<any[]> {
        const queryHead = await this._ensureTablesLoaded(['salesOrder', 'globalCustomers']);

        const salesOrderTable = getTableName('salesOrder');
        const escaped = docNo.replace(/'/g, '\'\'');
        const detailData = await queryHead.rawQuery(`
            SELECT ${COL.DOCNO} as docNo,
                TRY_CAST("SEQ" AS INTEGER) as seq,
                ${COL.CODE} as code,
                ${COL.ITEMCODE} as itemCode,
                ${COL.AGENT} as agent,
                TRY_CAST(${COL.DOCDATE} AS BIGINT) as docDate,
                "DELIVERYDATE" as deliveryDate,
                TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                TRY_CAST(${COL.QUANTITY} AS DOUBLE) as quantity,
                TRY_CAST("OSQTY" AS DOUBLE) as osQty,
                TRY_CAST(${COL.DISCOUNT} AS DOUBLE) as discount
            FROM ${salesOrderTable}
            WHERE ${COL.DOCNO} = '${escaped}'
        `);

        const items = detailData.map((row: any) => {
            const amt = Number(row.amount) || 0;
            const qty = Number(row.quantity) || 0;
            const osQty = Number(row.osQty) || 0;
            return {
                sequence: Number(row.seq) || 0,
                customer: gdbx.customerNames[String(row.code)],
                docDate: Number(row.docDate),
                item: gdbx.stockItemNames[String(row.itemCode)],
                delivery_date: osQty
                    ? moment(Number(row.deliveryDate)).format('DD/MM/YYYY')
                    : null,
                unit_price: qty ? amt / qty : 0,
                discount: Number(row.discount)
                    ? Math.ceil(Number(row.discount)) : null,
                amount: amt,
                os_amount: osQty === 0 || qty === 0
                    ? 0 : (amt / qty) * osQty,
                quantity: qty,
                os_quantity: osQty,
                _rowVariant: osQty > 0 ? 'top-five' : '',
                agent: String(row.agent),
            };
        });
        return items;
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        const salesOrderTable = getTableName('salesOrder');

        // Ensure all cross-table dependencies are loaded
        const queryHead = await this._ensureTablesLoaded(['salesOrder', 'globalCustomers']);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Build filter clauses manually
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');

        let agentFilter = '';
        if (this.selectedAgents.length > 0) {
            const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
            agentFilter = ` AND ${COL.AGENT} IN (${list})`;
        }

        const globalCustomersTable = getTableName('globalCustomers');
        let filterClause = '';

        if (this.selectedAreas.length > 0) {
            const areaList = this.selectedAreas.map((a) => `'${escapeStr(a)}'`).join(',');
            filterClause += ` AND ${COL.CODE} IN (
                SELECT "CODE" FROM ${globalCustomersTable} WHERE "AREA" IN (${areaList})
            )`;
        }

        if (this.selectedCustomers.length > 0) {
            const customerList = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
            filterClause += ` AND ${COL.CODE} IN (${customerList})`;
        }

        // Run summary query (detail loaded on click via loadDetailForDoc)
        const summaryData = await queryHead.rawQuery(`
            SELECT ${COL.DOCNO} as docNo,
                MIN(TRY_CAST(${COL.DOCDATE} AS BIGINT)) as docDate,
                MIN(${COL.CODE}) as code,
                MIN(${COL.AGENT}) as agent,
                SUM(TRY_CAST(${COL.AMOUNT} AS DOUBLE)) as amount,
                SUM(CASE
                    WHEN TRY_CAST(${COL.QUANTITY} AS DOUBLE) > 0
                    THEN (TRY_CAST(${COL.AMOUNT} AS DOUBLE)
                        / TRY_CAST(${COL.QUANTITY} AS DOUBLE))
                        * TRY_CAST("OSQTY" AS DOUBLE)
                    ELSE 0 END) as osAmount,
                COUNT(DISTINCT "SEQ") as itemCount,
                COUNT(DISTINCT CASE
                    WHEN TRY_CAST("OSQTY" AS DOUBLE) > 0
                    THEN "SEQ" END) as osItemCount
            FROM ${salesOrderTable}
            WHERE 1=1${agentFilter}${filterClause}
            GROUP BY ${COL.DOCNO}
            HAVING COUNT(DISTINCT CASE
                WHEN TRY_CAST("OSQTY" AS DOUBLE) > 0
                THEN "SEQ" END) > 0
            ORDER BY MIN(TRY_CAST(${COL.DOCDATE} AS BIGINT)) ASC, ${COL.DOCNO} ASC
        `);

        // Build table items (detail loaded on click via loadDetailForDoc)
        this.tableItems = summaryData.map((row: any) => {
            const docNo = String(row.docNo);
            const code = String(row.code);
            const docDate = Number(row.docDate);
            return {
                doc_no: docNo,
                doc_date: docDate,
                extras: `- ${moment(docDate).format('DD/MM/YYYY')}
						- ${gdbx.customerNames[code]}`,
                customer: gdbx.customerNames[code],
                amount: Number(row.amount) || 0,
                os_amount: Number(row.osAmount) || 0,
                item: Number(row.itemCount) || 0,
                os_item: Number(row.osItemCount) || 0,
                items: [],
                agent: String(row.agent),
            };
        });

        // Compute totals client-side from full summary
        this.amountSum = this.tableItems.reduce((a, b) => a + b.amount, 0);
        this.osAmountSum = this.tableItems.reduce((a, b) => a + b.os_amount, 0);
        this.periodTotalData = [
            {
                order_amt: this.currency + ' '
                    + addComma(this.amountSum, gdbx.numDecimal, true),
                os_amt: this.currency + ' '
                    + addComma(this.osAmountSum, gdbx.numDecimal, true),
                No_of_os_order: this.tableItems.length,
                No_of_os_customer: [
                    ...new Set(this.tableItems.map((item) => item.customer)),
                ].length,
            },
        ];
        this.generateLoadingText(0.95);

        this.tableFields = [
            {key: 'doc_no', sortable: true},
            {
                key: 'doc_date',
                sortable: true,
                formatter: TableItemFormatter.date3,
            },
            {
                key: 'customer',
                sortable: true,
            },
            {
                key: 'os_item',
                label: 'O/S Item',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'item',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'amount',
                label: 'Amt',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'os_amount',
                label: 'O/S Amount',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'agent',
                label: 'O/S Item',
                sortable: true,
            },
        ];

        this.detailFields = [
            {
                key: 'sequence', label: 'Seq',
                sortable: true, formatter: TableItemFormatter.basicInteger,
            },
            {key: 'item', sortable: true},
            {key: 'delivery_date', sortable: true},
            {
                key: 'os_quantity', label: 'O/S Qty',
                sortable: true, formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'quantity', label: 'Qty',
                sortable: true, formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'unit_price',
                sortable: true, formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'os_amount', label: 'O/S Amt',
                sortable: true, formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'amount', label: 'Amt',
                sortable: true, formatter: TableItemFormatter.currencySplit,
            },
        ];

        this.saveHistory('tableItems', 'tableFields', 'detailFields', 'periodTotalData');
    }

}
